import { useState, useEffect, useRef, ReactNode } from "react";
import { motion, useMotionValue } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ArrowDown,
  ArrowRight,
  Play,
  Sparkles,
  Users,
  Clock,
  TrendingUp,
  Activity,
  Check,
  Gem,
  CheckCircle,
  Plug,
  Eye
} from "lucide-react";
import MetaTags from "../components/SEO/MetaTags";
import { pageConfigs } from "../utils/seo";
import SilkBackdrop from "../components/ui/SilkBackdrop";
import OrganizationSchema from "../components/SEO/SchemaMarkup/Organization";
import SoftwareApplicationSchema from "../components/SEO/SchemaMarkup/SoftwareApplication";
import NotationHeroShowcase from "../components/notation/NotationHeroShowcase";
import NotationTransformsCare from "../components/notation/NotationTransformsCare";

// Typewriter effect hook
const useTypewriter = (text: string, speed: number = 50) => {
  const [displayText, setDisplayText] = useState('n'); // Start with 'n' already visible
  const [currentIndex, setCurrentIndex] = useState(1); // Start from index 1 (after 'n')
  const [isComplete, setIsComplete] = useState(false);
  const [hasStarted, setHasStarted] = useState(false); // Add delay before starting

  useEffect(() => {
    // Add initial delay before starting to type the rest
    if (!hasStarted) {
      const initialDelay = setTimeout(() => {
        setHasStarted(true);
      }, 800); // 800ms pause showing just "n"
      return () => clearTimeout(initialDelay);
    }

    if (hasStarted && currentIndex < text.length && !isComplete) {
      const timeout = setTimeout(() => {
        setDisplayText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, speed);

      return () => clearTimeout(timeout);
    } else if (currentIndex >= text.length) {
      setIsComplete(true);
    }
  }, [currentIndex, text, speed, isComplete, hasStarted]);

  return { displayText, isComplete };
};

interface MagneticButtonProps {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
}

const MagneticButton = ({ children, className = "", style, onClick }: MagneticButtonProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    x.set((e.clientX - centerX) * 0.1);
    y.set((e.clientY - centerY) * 0.1);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      style={{ x, y, ...style }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const cardsData = [
  {
    icon: Sparkles,
    title: "Smarter Documentation",
    description: "AI listens during sessions and generates accurate SOAP notes automatically.",
    metric: "2-3 hours saved daily"
  },
  {
    icon: Eye,
    title: "Vision-Powered Insights",
    description: "Real-time audio capture and motion analysis using built-in laptop mic & camera.",
    metric: "Reduce errors by 50%"
  },
  {
    icon: TrendingUp,
    title: "Predictive Intelligence",
    description: "Optimize billing, care plans, and documentation with AI-powered suggestions.",
    metric: "$50-70k savings"
  },
  {
    icon: Activity,
    title: "PT in the Loop",
    description: "Adaptive AI that learns therapist preferences for personalized documentation.",
    metric: "Reduce PT burnout"
  },
  {
    icon: Plug,
    title: "Seamless EMR Integration",
    description: "Copy SOAP notes directly to your favorite EMR with our browser extension.",
    metric: "Zero manual data entry"
  }
];

export default function Notation() {
  const navigate = useNavigate();
  const { displayText: notationText, isComplete } = useTypewriter("notation", 200);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [currentVideoSrc, setCurrentVideoSrc] = useState("");
  const demoSectionRef = useRef<HTMLElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // The hero showcase straddles the hero/section seam (translate-y-1/2), so the
  // hero's bottom padding and the next section's top padding must reserve half
  // its height. That height varies with the web/mobile toggle and viewport, so
  // measure it live instead of hard-coding a clamp (prevents mobile overlap).
  const showcaseRef = useRef<HTMLDivElement>(null);
  const [seamOverlap, setSeamOverlap] = useState(220);

  useEffect(() => {
    const el = showcaseRef.current;
    if (!el) return;
    const update = () => setSeamOverlap(el.offsetHeight / 2);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  const seamSpacing = `calc(${seamOverlap}px + 3rem)`;

  const openVideoModal = (videoSrc: string) => {
    setCurrentVideoSrc(videoSrc);
    setIsVideoModalOpen(true);
  };

  const closeVideoModal = () => {
    setIsVideoModalOpen(false);
    setCurrentVideoSrc("");
  };

  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const scrollToDemo = () => {
    if (demoSectionRef.current) {
      demoSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };


  // Handle hash-based scrolling on page load
  useEffect(() => {
    const handleHashScroll = () => {
      const hash = window.location.hash;
      if (hash === '#hero') {
        // Add a small delay to ensure the page is fully rendered
        setTimeout(() => {
          const heroSection = document.getElementById('hero');
          if (heroSection) {
            heroSection.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else if (hash === '#pricing') {
        // Add a small delay to ensure the page is fully rendered
        setTimeout(() => {
          const pricingSection = document.getElementById('pricing');
          if (pricingSection) {
            pricingSection.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    };

    // Handle initial load
    handleHashScroll();

    // Handle hash changes (in case user manually changes URL)
    window.addEventListener('hashchange', handleHashScroll);

    return () => {
      window.removeEventListener('hashchange', handleHashScroll);
    };
  }, []);

  return (
    <>
      <MetaTags
        title={pageConfigs.notation.title}
        description={pageConfigs.notation.description}
        keywords={pageConfigs.notation.keywords}
        canonical={pageConfigs.notation.canonical}
      />
      <OrganizationSchema />
      <SoftwareApplicationSchema />
      
      <div className="min-h-screen overflow-x-hidden relative fownd-window-surface">
        {/* Video Modal */}
        {isVideoModalOpen && (
          <div 
            className="fixed inset-0 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(30, 61, 59, 0.9)' }}
            onClick={closeVideoModal}
          >
            <div 
              className="relative w-full max-w-5xl h-auto rounded-lg overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              style={{ width: '65vw', height: '65vh', backgroundColor: '#1e3d3b' }}
            >
              <button
                onClick={closeVideoModal}
                className="absolute top-4 right-4 z-10 text-white bg-black/50 hover:bg-black/70 rounded-full p-2 transition-colors duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <video
                className="w-full h-full object-contain"
                autoPlay
                loop
                muted
                playsInline
                controls
              >
                <source src={currentVideoSrc} type="video/mp4" />
              </video>
            </div>
          </div>
        )}

        {/* Hero Section - Dark with Gradient */}
        <section
          id="hero"
          className="relative px-4 sm:px-6 lg:px-8 overflow-visible bg-gradient-to-br from-[#1F3D3B] via-[#1F3D3B] to-[#495B25]"
          style={{ paddingBottom: seamSpacing }}
        >
          {/* Animated silk — blended over the hero gradient */}
          <SilkBackdrop opacity={0.5} />
          {/* Floating Orbs */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 0.4 }}
              transition={{ duration: 2 }}
              className="absolute top-20 left-20 w-60 h-60 bg-[#619ed3]/20 rounded-full blur-3xl pointer-events-none"
            />
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 0.3 }}
              transition={{ duration: 2, delay: 1 }}
              className="absolute bottom-20 right-20 w-80 h-80 bg-[#5c91a4]/20 rounded-full blur-3xl pointer-events-none"
            />
          </div>

          {/* Hero copy + CTAs */}
          <div className="relative z-10 max-w-6xl mx-auto text-center pt-28 md:pt-32 lg:pt-36">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.4 }}
              className="mb-6 md:mb-8"
            >
              <div className="inline-flex items-center text-white text-base mb-4 font-semibold font-productSans">
                <img
                  src="/assets/images/home/fownd.png"
                  alt="Fownd"
                  className="w-5 h-5 mr-2 brightness-0 invert opacity-90"
                />
                Powered by Fownd
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold mb-4 tracking-tight text-white font-productSans leading-tight">
                <span>Introducing{" "}</span>
                <span
                  className="text-[#439CCB] relative inline-block"
                  style={{ fontFamily: "'Baloo Bhaina 2', sans-serif", fontSize: "1.2em" }}
                >
                  {notationText}
                  {!isComplete && (
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: [1, 0] }}
                      transition={{ duration: 0.8, repeat: Infinity, repeatType: "reverse" }}
                      className="text-white"
                    >
                      |
                    </motion.span>
                  )}
                </span>
              </h1>

              <p className="text-sm sm:text-base md:text-lg text-white/85 font-productSans max-w-3xl mx-auto leading-relaxed font-light">
                Your AI Clinical Documentation Partner, that fits naturally in your workflow.
              </p>
              <p className="text-sm md:text-base text-[#619ed3] mt-3 font-medium font-productSans">
                You Treat • We Document
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.9 }}
              className="flex flex-col items-center gap-5 mb-12 md:mb-16 lg:mb-20"
            >
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center w-full">
                <MagneticButton
                  onClick={() => window.open("https://notation.fownd.care/", "_blank")}
                  className="group relative text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-110 hover:shadow-2xl bg-gradient-to-r from-[#58ACDD] to-[#486633] hover:from-[#4a9bc8] hover:to-[#3d5529] cursor-pointer font-productSans w-full sm:w-auto"
                >
                  <span className="flex items-baseline justify-center gap-2">
                    <span>
                      Start{" "}
                      <span
                        className="leading-none"
                        style={{ fontFamily: "'Baloo Bhaina 2', sans-serif", fontSize: "1.08em" }}
                      >
                        notation
                      </span>
                    </span>
                    <ArrowRight className="w-5 h-5 self-center group-hover:translate-x-2 transition-transform duration-300" />
                  </span>
                  <div className="absolute inset-0 bg-white/30 rounded-full blur opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </MagneticButton>

                <MagneticButton
                  onClick={scrollToDemo}
                  className="group relative px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-110 border-2 border-white/30 text-white hover:bg-white/20 hover:border-white/60 cursor-pointer font-productSans w-full sm:w-auto"
                >
                  <span className="flex items-center justify-center gap-2">
                    <ArrowDown className="w-5 h-5 group-hover:translate-y-1 transition-transform duration-300" />
                    Learn More
                  </span>
                </MagneticButton>
              </div>

              <p className="text-white/60 text-sm font-productSans">
                Instant Access • No Sign-Up • HIPAA Compliant
              </p>
            </motion.div>
          </div>

          {/* Showcase straddles hero/cream seam — vertical center on the curve (Fellow-style) */}
          <div
            ref={showcaseRef}
            className="absolute left-1/2 bottom-0 z-30 w-full max-w-7xl px-4 sm:px-6 -translate-x-1/2 translate-y-1/2"
          >
            <NotationHeroShowcase />
          </div>

          {/* Curved divider — hero green sweeps downward; cream shape is the section below */}
          <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none pointer-events-none z-20">
            <svg viewBox="0 0 1440 120" preserveAspectRatio="none" className="w-full h-[70px] md:h-[110px]">
              <defs>
                <linearGradient id="notationHeroDivider" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#ffffff" />
                  <stop offset="100%" stopColor="#ffffff" />
                </linearGradient>
              </defs>
              <path d="M0,0 Q720,120 1440,0 L1440,121 L0,121 Z" fill="url(#notationHeroDivider)" />
            </svg>
          </div>
        </section>

        {/* Challenge & Solution — moved from Home */}
        <section
          className="relative pb-4 px-4 sm:px-6 lg:px-8 fownd-window-surface"
          style={{ paddingTop: seamSpacing }}
        >
          <div className="max-w-7xl mx-auto relative z-20">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 max-w-7xl mx-auto">
              {cardsData.map((card, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center group cursor-pointer relative p-4"
                >
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-b from-[#7DD3FC]/30 to-[#166534]/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl"></div>
                  <div className="relative z-10 group-hover:scale-105 transition-transform duration-300">
                    <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-gradient-to-b from-[#7DD3FC] to-[#166534] group-hover:from-[#5EEAD4] group-hover:to-[#064E3B] transition-all duration-300 shadow-lg group-hover:shadow-xl group-hover:shadow-[#7DD3FC]/40">
                      <card.icon className="w-10 h-10 text-white group-hover:scale-110 transition-transform duration-300" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 font-productSans mb-3 group-hover:text-[#166534] transition-colors duration-300">
                      {card.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed font-dmSans group-hover:text-gray-700 transition-colors duration-300">
                      {card.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* How Notation Transforms Care Section */}
        <NotationTransformsCare />

        {/* Features Section - Like Home Page */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 fownd-window-surface">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <div className="inline-flex items-center text-[#495B25] text-base mb-6 font-semibold">
                <Sparkles className="w-4 h-4 mr-2" />
                Core Features
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 tracking-tight text-gray-900 font-productSans">
                Built for{" "}
                <span className="bg-gradient-to-r from-[#619ed3] to-[#5c91a4] bg-clip-text text-transparent">
                  Clinicians
                </span>
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Experience the power of AI-driven clinical documentation designed specifically for your workflow
              </p>
            </motion.div>

            {/* Feature Grid with Descriptions - Card Layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              
              {/* Documentation Feature */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0 * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl hover:scale-105 transition-all duration-300 group flex flex-col h-full"
              >
                <div className="text-center flex-grow">
                  <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-gradient-to-b from-[#7DD3FC] to-[#166534] group-hover:from-[#5EEAD4] group-hover:to-[#064E3B] transition-all duration-300 shadow-lg group-hover:shadow-xl group-hover:shadow-[#7DD3FC]/40">
                    <Sparkles className="w-10 h-10 text-white group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 font-productSans mb-3 group-hover:text-[#166534] transition-colors duration-300">
                    Documentation, Powered by AI
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed font-dmSans group-hover:text-gray-700 transition-colors duration-300 mb-4">
                    AI listens during sessions and generates accurate SOAP notes automatically.
                  </p>
                  
                  {/* Feature list */}
                  <div className="space-y-2 text-left text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" style={{ color: '#2563eb' }} />
                      <span>Real-time capture of patient conversations</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" style={{ color: '#2563eb' }} />
                      <span>Medical LLMs extract clinical details</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" style={{ color: '#2563eb' }} />
                      <span>Reduces documentation workload</span>
                    </div>
                  </div>
                </div>
                
                {/* Metric badge - positioned at bottom */}
                <div className="mt-auto pt-4 text-center">
                  <div className="inline-block px-4 py-2 rounded-full text-sm font-medium shadow-lg text-white" style={{ backgroundColor: '#619ed3', color: '#ffffff' }}>
                    2-3 hours saved daily
                  </div>
                </div>
              </motion.div>

              {/* Machine Learning Feature */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 1 * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl hover:scale-105 transition-all duration-300 group flex flex-col h-full"
              >
                <div className="text-center flex-grow">
                  <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-gradient-to-b from-[#7DD3FC] to-[#166534] group-hover:from-[#5EEAD4] group-hover:to-[#064E3B] transition-all duration-300 shadow-lg group-hover:shadow-xl group-hover:shadow-[#7DD3FC]/40">
                    <svg className="w-10 h-10 text-white group-hover:scale-110 transition-transform duration-300" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                      <path fillRule="evenodd" d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 010-1.113zM17.25 12a5.25 5.25 0 11-10.5 0 5.25 5.25 0 0110.5 0z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 font-productSans mb-3 group-hover:text-[#166534] transition-colors duration-300">
                    Machine Learning & Computer Vision Analytics
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed font-dmSans group-hover:text-gray-700 transition-colors duration-300 mb-4">
                    Real-time audio capture and motion analysis using built-in laptop mic & camera.
                  </p>
                  
                  {/* Feature list */}
                  <div className="space-y-2 text-left text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" style={{ color: '#2563eb' }} />
                      <span>Runs alongside any EMR</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" style={{ color: '#2563eb' }} />
                      <span>No extra hardware needed</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" style={{ color: '#2563eb' }} />
                      <span>Accurately capture, analyze, and track a patient's movement over time</span>
                    </div>
                  </div>
                </div>
                
                {/* Metric badge - positioned at bottom */}
                <div className="mt-auto pt-4 text-center">
                  <div className="inline-block px-4 py-2 rounded-full text-sm font-medium shadow-lg text-white" style={{ backgroundColor: '#619ed3', color: '#ffffff' }}>
                    Reduce errors by 50%
                  </div>
                </div>
              </motion.div>

              {/* Predictive Intelligence Feature */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 2 * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl hover:scale-105 transition-all duration-300 group flex flex-col h-full"
              >
                <div className="text-center flex-grow">
                  <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-gradient-to-b from-[#7DD3FC] to-[#166534] group-hover:from-[#5EEAD4] group-hover:to-[#064E3B] transition-all duration-300 shadow-lg group-hover:shadow-xl group-hover:shadow-[#7DD3FC]/40">
                    <TrendingUp className="w-10 h-10 text-white group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 font-productSans mb-3 group-hover:text-[#166534] transition-colors duration-300">
                    Predictive Intelligence for PTs
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed font-dmSans group-hover:text-gray-700 transition-colors duration-300 mb-4">
                    Optimize billing, care plans, and documentation with AI-powered suggestions.
                  </p>
                  
                  {/* Feature list */}
                  <div className="space-y-2 text-left text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" style={{ color: '#2563eb' }} />
                      <span>Automated billing codes that can be modified to meet your needs</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" style={{ color: '#2563eb' }} />
                      <span>Suggested interventions, with a built in HEP creator</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" style={{ color: '#2563eb' }} />
                      <span>SMART goals setting that meets insurance requirements</span>
                    </div>
                  </div>
                </div>
                
                {/* Metric badge - positioned at bottom */}
                <div className="mt-auto pt-4 text-center">
                  <div className="inline-block px-4 py-2 rounded-full text-sm font-medium shadow-lg text-white" style={{ backgroundColor: '#619ed3', color: '#ffffff' }}>
                    $50-70k savings
                  </div>
                </div>
              </motion.div>

              {/* PT-Enabled Feedback Loop Feature */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 3 * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl hover:scale-105 transition-all duration-300 group flex flex-col h-full"
              >
                <div className="text-center flex-grow">
                  <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-gradient-to-b from-[#7DD3FC] to-[#166534] group-hover:from-[#5EEAD4] group-hover:to-[#064E3B] transition-all duration-300 shadow-lg group-hover:shadow-xl group-hover:shadow-[#7DD3FC]/40">
                    <Activity className="w-10 h-10 text-white group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 font-productSans mb-3 group-hover:text-[#166534] transition-colors duration-300">
                    PT-Enabled Feedback Loop
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed font-dmSans group-hover:text-gray-700 transition-colors duration-300 mb-4">
                    Adaptive AI that learns therapist preferences for personalized documentation.
                  </p>
                  
                  {/* Feature list */}
                  <div className="space-y-2 text-left text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" style={{ color: '#2563eb' }} />
                      <span>Fully customizable notes</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" style={{ color: '#2563eb' }} />
                      <span>Learns & adapts over time</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" style={{ color: '#2563eb' }} />
                      <span>HIPAA-compliant by design</span>
                    </div>
                  </div>
                </div>
                
                {/* Metric badge - positioned at bottom */}
                <div className="mt-auto pt-4 text-center">
                  <div className="inline-block px-4 py-2 rounded-full text-sm font-medium shadow-lg text-white" style={{ backgroundColor: '#619ed3', color: '#ffffff' }}>
                    Reduce PT burnout
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Demo Section - A Complete Look into Notation */}
        <section ref={demoSectionRef} className="relative py-20 px-4 sm:px-6 lg:px-8 fownd-window-surface">
          <div className="max-w-5xl mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <div className="inline-flex items-center text-[#495B25] text-base mb-6 font-semibold">
                <Play className="w-4 h-4 mr-2" />
                Live Demo
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 tracking-tight text-gray-900 font-productSans">
                A Complete Look into{" "}
                <span
                  className="text-[#439CCB]"
                  style={{ fontFamily: "'Baloo Bhaina 2', sans-serif", fontSize: "1.2em" }}
                >
                  notation
                </span>
              </h2>
              <p className="text-lg text-gray-600 font-light">
                See how AI transforms clinical documentation
              </p>
              <p className="mt-2 text-sm md:text-base text-gray-600 italic">
                Presented by <span className="font-medium text-gray-800">Dr. Christina Rama, PT, DPT</span>
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="bg-gray-50 border border-gray-200 overflow-hidden shadow-2xl group rounded-2xl relative cursor-pointer">
                <div className="p-0 relative">
                  <video
                    ref={videoRef}
                    className="w-full h-full object-cover z-0"
                    controls
                    preload="auto"
                    playsInline
                    onPause={handlePause}
                  >
                    <source src="https://s3.us-east-1.amazonaws.com/fownd.care/Home+Videos/DemoV3_voice.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>

                  {/* Overlay Play Button */}
                  {!isPlaying && (
                    <div
                      className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/10 transition-opacity duration-300"
                      onClick={handlePlay}
                    >
                      <div className="w-16 h-16 bg-[#619ed3]/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                        <Play className="w-8 h-8 text-[#619ed3]" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
            
            {/* Enhanced CTA Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-center mt-12"
            >
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 font-productSans">
                Ready to experience{" "}
                <span className="bg-gradient-to-r from-[#619ed3] to-[#5c91a4] bg-clip-text text-transparent" style={{ fontFamily: "'Baloo Bhaina 2', sans-serif", fontSize: '1.2em' }}>notation</span>
                {" "}yourself?
              </h3>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                Join hundreds of clinicians who have transformed their documentation workflow
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a
                  href="https://bookings.cloud.microsoft/book/NotationAI@fownd.org/?ismsaljsauthenabled"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group w-full sm:w-auto"
                >
                  <button className="w-full sm:w-[280px] h-12 bg-[#619ed3] text-white px-8 rounded-full font-semibold hover:bg-[#5c91a4] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 inline-flex items-center justify-center gap-2">
                    <Users className="w-4 h-4 shrink-0" />
                    Schedule Personal Demo
                  </button>
                </a>
                
                <a
                  href="https://notation.fownd.care/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group w-full sm:w-auto"
                >
                  <button className="w-full sm:w-[280px] h-12 bg-gray-100 text-gray-900 px-8 rounded-full font-semibold hover:bg-gray-200 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 inline-flex items-center justify-center gap-2 border border-gray-300 box-border">
                    <Sparkles className="w-4 h-4 shrink-0" />
                    <span>
                      Try{" "}
                      <span
                        className="leading-none"
                        style={{ fontFamily: "'Baloo Bhaina 2', sans-serif", fontSize: "1.1em", color: "#439CCB" }}
                      >
                        notation
                      </span>{" "}
                      Free
                    </span>
                  </button>
                </a>
              </div>
              
              <p className="text-gray-500 text-sm mt-4">
                Start instantly, no sign-up needed
              </p>
            </motion.div>
          </div>
        </section>

        {/* Pricing & FAQ Section - Combined Background */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#1e3d3b] via-[#2a5a56] to-[#1e3d3b]">
          {/* Animated silk — blended over the pricing/FAQ gradient */}
          <SilkBackdrop opacity={0.5} />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-transparent via-[#1e3d3b]/10 to-[#1e3d3b]/20 z-0"></div>

          {/* Curved divider — green rises upward; white shape is the real section color above */}
          <div className="absolute top-0 left-0 w-full overflow-hidden leading-none pointer-events-none z-10">
            <svg viewBox="0 0 1440 120" preserveAspectRatio="none" className="w-full h-[70px] md:h-[110px]">
              <path d="M0,-1 L1440,-1 L1440,120 Q720,0 0,120 Z" fill="#ffffff" />
            </svg>
          </div>

          {/* Pricing Section */}
          <div id="pricing" className="relative pt-28 pb-20 px-6 z-20">
            <div className="max-w-7xl mx-auto relative z-20">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="text-center mb-16"
              >
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-productSans">
                  Plans and Pricing
                </h2>
                <p className="text-xl text-slate-200 max-w-3xl mx-auto leading-relaxed font-dmSans">
                  Choose which plan is best for your needs to continue using Notation AI
                </p>
                
                {/* Early Adopter Badge */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3, duration: 0.6 }}
                  className="inline-block mt-8 bg-[#CFCBBA] text-[#1F3D3B] px-6 py-3 rounded-full font-bold text-lg shadow-lg"
                >
                  Early Adopter Perks - Free for Limited Period
                </motion.div>
              </motion.div>

            {/* Pricing Cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              
              {/* Pay-As-You-Go Plan */}
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ 
                  scale: 1.02, 
                  y: -4, 
                  boxShadow: "0 20px 40px -12px rgba(0, 0, 0, 0.2)" 
                }}
                viewport={{ once: true }}
                transition={{ 
                  delay: 0.1, 
                  duration: 0.6,
                  type: "spring",
                  stiffness: 100,
                  damping: 15
                }}
                className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-xl relative cursor-pointer flex flex-col h-full"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-slate-800 text-lg font-productSans">Pay-As-You-Go</h3>
                  <div className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm font-semibold">
                    40% off
                  </div>
                </div>
                
                <div className="mb-6">
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-slate-800">$5</span>
                    <span className="text-slate-600 font-dmSans">/ session</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-6 text-sm flex-grow">
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700 font-dmSans">Ideal for occasional or trial users</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700 font-dmSans">No monthly membership fee</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700 font-dmSans">Pay $5 per session</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700 font-dmSans">No limit on sessions</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700 font-dmSans">HIPAA & SOC compliant infrastructure</span>
                  </li>
                </ul>

                <button
                  onClick={() => window.open("https://notation.fownd.care/", "_blank")}
                  className="w-full bg-[#619DD2] text-white py-3 rounded-lg font-semibold hover:bg-[#5a8bc2] transition-colors duration-200 cursor-pointer flex items-center justify-center mt-auto"
                >
                  Free Now!
                </button>
              </motion.div>

              {/* Starter Plan */}
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ 
                  scale: 1.02, 
                  y: -4, 
                  boxShadow: "0 20px 40px -12px rgba(0, 0, 0, 0.2)" 
                }}
                viewport={{ once: true }}
                transition={{ 
                  delay: 0.2, 
                  duration: 0.6,
                  type: "spring",
                  stiffness: 100,
                  damping: 15
                }}
                className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-xl relative cursor-pointer flex flex-col h-full"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-slate-800 text-lg font-productSans">Starter</h3>
                  <div className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm font-semibold">
                    60% off
                  </div>
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-slate-800">$2</span>
                    <span className="text-slate-600 font-dmSans">/ session</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-6 text-sm flex-grow">
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700 font-dmSans">Ideal for light caseloads or part time PTs</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700 font-dmSans">$39 monthly fee; cancel anytime</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700 font-dmSans">20 sessions included free each month</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700 font-dmSans">Only $2/session</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700 font-dmSans">HIPAA & SOC compliant infrastructure</span>
                  </li>
                </ul>

                <button
                  onClick={() => window.open("https://notation.fownd.care/", "_blank")}
                  className="w-full bg-[#619DD2] text-white py-3 rounded-lg font-semibold hover:bg-[#5a8bc2] transition-colors duration-200 cursor-pointer flex items-center justify-center mt-auto"
                >
                  Free Now!
                </button>
              </motion.div>

              {/* Pro Plan - Most Popular */}
              <motion.div
                initial={{ opacity: 0, y: 50, scale: 1.05 }}
                whileInView={{ opacity: 1, y: 0, scale: 1.05 }}
                whileHover={{ 
                  scale: 1.07, 
                  y: -6
                }}
                viewport={{ once: true }}
                transition={{ 
                  delay: 0.3, 
                  duration: 0.6,
                  type: "spring",
                  stiffness: 100,
                  damping: 15
                }}
                className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-xl relative cursor-pointer flex flex-col h-full"
                style={{
                  background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, rgba(67, 156, 203, 0.85), rgba(73, 91, 37, 0.65)) border-box',
                  border: '2px solid transparent',
                  boxShadow: '0 0 30px rgba(67, 156, 203, 0.25), 0 10px 25px -5px rgba(0, 0, 0, 0.1)'
                }}
              >
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                  <div className="bg-gradient-to-r from-[#58ACDD] to-[#486633] text-white px-4 py-2 rounded-full text-sm font-bold text-center shadow-md">
                    Most Popular
                  </div>
                </div>

                <div className="flex items-center justify-between mb-4 mt-4">
                  <h3 className="font-bold text-slate-800 text-lg font-productSans">Pro</h3>
                  <div className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm font-semibold">
                    65% off
                  </div>
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-slate-800">$1.75</span>
                    <span className="text-slate-600 font-dmSans">/ session</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-6 text-sm flex-grow">
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700 font-dmSans">Ideal for active solo practitioners</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700 font-dmSans">$69 monthly fee; cancel anytime</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700 font-dmSans">40 sessions included free each month</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700 font-dmSans">Only $1.75/session</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700 font-dmSans">6 months free premium support</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700 font-dmSans">HIPAA & SOC compliant infrastructure</span>
                  </li>
                </ul>

                <button
                  onClick={() => window.open("https://notation.fownd.care/", "_blank")}
                  className="w-full bg-gradient-to-r from-[#58ACDD] to-[#486633] text-white py-3 rounded-lg font-bold hover:from-[#4a9bc8] hover:to-[#3d5529] hover:shadow-lg transition-all duration-200 cursor-pointer flex items-center justify-center mt-auto"
                >
                  Free Now!
                </button>
              </motion.div>

              {/* Enterprise Plan */}
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ 
                  scale: 1.02, 
                  y: -4, 
                  boxShadow: "0 20px 40px -12px rgba(0, 0, 0, 0.2)" 
                }}
                viewport={{ once: true }}
                transition={{ 
                  delay: 0.4, 
                  duration: 0.6,
                  type: "spring",
                  stiffness: 100,
                  damping: 15
                }}
                className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-xl relative cursor-pointer flex flex-col h-full"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-slate-800 text-lg font-productSans">Enterprise</h3>
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-slate-800">Custom Pricing</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-6 text-sm flex-grow">
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700 font-dmSans">Ideal for clinics, hospitals, and multi-provider practices</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700 font-dmSans">Tailored pricing based on usage volume and team size</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700 font-dmSans">Dedicated onboarding & training</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700 font-dmSans">SLA-backed support and account manager</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700 font-dmSans">HIPAA & SOC compliant infrastructure & audit support</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700 font-dmSans">Integration with EMR / custom workflows</span>
                  </li>
                </ul>

                <button
                  onClick={() => window.open("https://outlook.office.com/book/NotationWalkthrough@fownd.org/?ismsaljsauthenabled", "_blank")}
                  className="w-full bg-[#619DD2] text-white py-3 rounded-lg font-semibold hover:bg-[#5a8bc2] transition-colors duration-200 cursor-pointer flex items-center justify-center mt-auto"
                >
                  Contact Us
                </button>
              </motion.div>
            </div>

            {/* Early Adopter Benefits */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="text-center mt-16"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 max-w-4xl mx-auto">
                <h3 className="text-2xl font-bold text-white mb-6 font-productSans flex items-center justify-center gap-2">
                  <Sparkles className="w-6 h-6 text-[#439CCB]" />
                  Early Adopter Benefits
                </h3>
                <div className="grid md:grid-cols-3 gap-6 text-slate-200">
                  <div className="flex flex-col items-center hover:-translate-y-1 transition-all duration-300 ease-out">
                    <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mb-3 cursor-pointer hover:scale-110 hover:rotate-3 transition-all duration-300 ease-out">
                      <Check className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="font-semibold mb-2">Free for Limited Period</h4>
                    <p className="text-sm">Full access to all features during our beta period</p>
                  </div>
                  <div className="flex flex-col items-center hover:-translate-y-1 transition-all duration-300 ease-out">
                    <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mb-3 cursor-pointer hover:scale-110 hover:-rotate-3 transition-all duration-300 ease-out">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <h4 className="font-semibold mb-2">Custom EMR Integration Support</h4>
                    <p className="text-sm">Early adopters can request integrations with their existing EMR systems</p>
                  </div>
                  <div className="flex flex-col items-center hover:-translate-y-1 transition-all duration-300 ease-out">
                    <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mb-3 cursor-pointer hover:scale-110 hover:rotate-3 transition-all duration-300 ease-out">
                      <Gem className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="font-semibold mb-2">Premium Support</h4>
                    <p className="text-sm">Direct access to our development team and priority support</p>
                  </div>
                </div>
                
                <div className="mt-8 p-4 bg-gradient-to-r from-[#439CCB]/15 to-[#495B25]/20 rounded-lg border border-[#439CCB]/30">
                  <p className="text-slate-100 font-semibold flex items-center justify-center gap-2">
                    <Clock className="w-5 h-5 text-[#439CCB]" />
                    Limited Time Offer: Start using Notation now for FREE and secure your early adopter pricing before our public launch!
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
          </div>
          
          {/* Subtle Separator */}
          <div className="relative flex justify-center py-8">
            <div className="w-24 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
          </div>
          
          {/* Modern FAQ Section - Compact Grid Design */}
          <div className="relative py-12 px-6 z-20">
            <div className="max-w-7xl mx-auto relative z-20">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-productSans">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-slate-200 max-w-2xl mx-auto leading-relaxed">
                Everything you need to know about Notation
              </p>
            </motion.div>

            {/* Three-Column Grid for Desktop, Single Column for Mobile */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
              {[
                {
                  question: "What is Notation?",
                  answer: "A real-time AI assistant that listens to your sessions and auto-generates compliant SOAP notes."
                },
                {
                  question: "How does Notation work?",
                  answer: "Uses ambient AI and natural language processing to capture clinically relevant information and draft SOAP notes instantly."
                },
                {
                  question: "Is Notation HIPAA-compliant?",
                  answer: "Yes. All data is encrypted and handled per HIPAA and healthcare data security standards."
                },
                {
                  question: "Does it work with our EMR?",
                  answer: "Yes, our browser extension allows you to copy SOAP notes directly to any web-based EMR with one click."
                },
                {
                  question: "How do I get the plugin?",
                  answer: "The plugin is automatically included with your Notation account. No separate download or installation needed."
                },
                {
                  question: "Which browsers support the plugin?",
                  answer: "Our extension works with Chrome, Safari, and Edge browsers for seamless EMR integration."
                },
                {
                  question: "What do I need to use it?",
                  answer: "Just a device with a microphone and internet connection. No special hardware required."
                },
                {
                  question: "Will this replace therapists?",
                  answer: "Never. Notation supports clinicians by handling documentation tasks, not clinical judgment or care."
                },
                {
                  question: "How do I get started?",
                  answer: "Click 'Get Started' or 'Schedule a Demo'. Our team will walk you through setup and training."
                },
                {
                  question: "How long is onboarding?",
                  answer: "Onboarding is instant—just create an account and you're ready to start using Notation."
                },
                {
                  question: "Is there a free trial?",
                  answer: "Yes! We offer a free trial so you can see Notation in action in your clinic before committing."
                },
                {
                  question: "How much does it cost?",
                  answer: "We offer flexible pricing based on clinic size and usage. Check our pricing section above."
                },
                {
                  question: "Are there enterprise discounts?",
                  answer: "Yes. We offer volume discounts and enterprise onboarding for larger organizations."
                },
                {
                  question: "Works with low Wi-Fi?",
                  answer: "Notation is lightweight and cloud-based. We'll help you test compatibility before you commit."
                },
                {
                  question: "What happens to session data?",
                  answer: "Session data is securely stored and accessible only to authorized users. Export, review, or delete anytime."
                },
                {
                  question: "Can I test it myself first?",
                  answer: "Absolutely. We encourage therapists to experience Notation firsthand—no pressure to commit."
                },
                {
                  question: "Can I use it individually?",
                  answer: "Yes, you can use Notation individually as a PT without your clinic needing to sign up."
                }
              ].map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-white/20 transition-all duration-300 hover:bg-white/10 group"
                >
                  <h3 className="text-white font-semibold text-lg mb-3 font-productSans group-hover:text-[#619ed3] transition-colors duration-300">
                    {faq.question}
                  </h3>
                  <p className="text-slate-300 text-sm leading-relaxed font-dmSans">
                    {faq.answer}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
          </div>
        </section>
      </div>
    </>
  );
} 